import React, { useState } from "react";
import { Button, Flex, Icon, Text } from "@chakra-ui/react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import ContactCardsHeader from "./ContactCardsHeader";
import ContactCardsSearchBar from "./ContactCardsSearchBar";
import ContactCardsTable from "./ContactCardsTable";
import ContactCardDetailDrawer from "./ContactCardDetailDrawer";
import useContactCardsList from "./hooks/useContactCardsList";
import useContactCardsSearch from "./hooks/useContactCardsSearch";

/**
 * Cursor-style pagination: previous/next buttons + current page indicator.
 * Hidden in search mode since /search returns up to 20 results unpaginated.
 * @param {Object} props - page (1-indexed), hasMore (boolean), onChange (fn)
 * @returns {React.ReactElement|null}
 */
function PaginationRow({ page, hasMore, onChange }) {
  if (page <= 1 && !hasMore) return null;
  return (
    <Flex justify="center" align="center" gap="10px" py="4px">
      <Button size="sm" variant="outline" colorScheme="brand" isDisabled={page <= 1}
        leftIcon={<Icon as={MdChevronLeft} />} onClick={() => onChange(Math.max(1, page - 1))}>
        Précédent
      </Button>
      <Text fontSize="sm" color="secondaryGray.600" _dark={{ color: "secondaryGray.400" }} px="8px">Page {page}</Text>
      <Button size="sm" variant="outline" colorScheme="brand" isDisabled={!hasMore}
        rightIcon={<Icon as={MdChevronRight} />} onClick={() => onChange(page + 1)}>
        Suivant
      </Button>
    </Flex>
  );
}

/**
 * Admin page for browsing and searching contact cards.
 * Toggles between paginated list and search results based on the query state.
 * @returns {React.ReactElement}
 */
function ContactCards() {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const list = useContactCardsList(page);
  const search = useContactCardsSearch(query);

  const items = search.active ? search.items : list.items;
  const loading = search.active ? search.loading : list.loading;
  const error = search.active ? search.error : list.error;

  return (
    <Flex direction="column" gap="20px">
      <ContactCardsHeader count={items.length} searchActive={search.active} />
      <ContactCardsSearchBar value={query} onChange={setQuery} />
      <ContactCardsTable items={items} loading={loading} error={error} onSelect={setSelected} />
      {!search.active && <PaginationRow page={page} hasMore={list.hasMore} onChange={setPage} />}
      <ContactCardDetailDrawer card={selected} onClose={() => setSelected(null)} />
    </Flex>
  );
}

export default ContactCards;
